import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { profileApi, type PersonalSupply, type GreenCertCreateParams } from '@/services/api';
import {
  formatFieldValue,
  YEAR_OPTIONS,
  HALF_YEAR_OPTIONS,
  MONTH_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  PROVINCE_OPTIONS,
  SUBSIDY_OPTIONS,
  TRADE_METHOD_OPTIONS,
  EXCHANGE_OPTIONS,
  OFFER_BY_OPTIONS,
  TRADE_PRINCIPLE_OPTIONS,
} from '@/components/feature/GreenCertFieldConfig';

interface MySupplyTabProps {
  role: 'supplier' | 'buyer';
}

function tagLabel(vals: string[]): string | null {
  if (!vals || vals.length === 0) return null;
  const limit = vals.slice(0, 2);
  return limit.map((v) => {
    if (v === 'non_channel') return '非通道';
    if (v === 'channel') return '通道';
    if (v === 'any') return '均可';
    if (v === 'subsidized') return '补贴';
    if (v === 'non_subsidized') return '非补贴';
    if (v === 'transfer') return '划转';
    if (v === 'certify') return '制证';
    if (v === 'price_priority') return '价格优先';
    if (v === 'time_priority') return '时间优先';
    return v;
  }).join('·');
}

function EditModal({
  item,
  onSaved,
  onClose,
}: {
  item: PersonalSupply;
  onSaved: (updated: PersonalSupply) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<GreenCertCreateParams>({
    direction: 'sell',
    year: item.year,
    halfYear: item.halfYear || [],
    month: item.month || [],
    projectType: item.projectType || [],
    channel: item.channel || [],
    provinceRequirement: item.provinceRequirement || [],
    subsidy: item.subsidy || [],
    quantity: item.quantity,
    price: item.price,
    tradeMethod: item.tradeMethod || [],
    exchange: item.exchange || [],
    offerBy: item.offerBy || [],
    tradePrinciple: item.tradePrinciple || [],
    expiryDate: item.expiryDate,
    otherInfo: item.otherInfo || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (field: keyof GreenCertCreateParams, value: string) => {
    setForm((prev) => {
      const arr = (prev[field] as string[]) || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const single = (field: keyof GreenCertCreateParams, value: string) => {
    setForm((prev) => ({ ...prev, [field]: [value] }));
  };

  const chips = (
    field: keyof GreenCertCreateParams,
    label: string,
    options: { value: string; label: string }[],
    multi: boolean,
  ) => {
    const arr = (form[field] as string[]) || [];
    return (
      <div>
        <label className="block text-xs font-medium text-foreground-600 mb-1.5">
          {label}
          {multi && <span className="text-foreground-400 font-normal ml-1">可多选</span>}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const selected = arr.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => (multi ? toggle(field, opt.value) : single(field, opt.value))}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  selected
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-background-200 text-foreground-500 hover:border-background-300'
                }`}
              >
                {selected && multi && <i className="ri-check-line mr-0.5" />}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const save = async () => {
    if (!form.year) { setError('请选择年份'); return; }
    if (form.halfYear.length === 0) { setError('请选择半年'); return; }
    if (form.projectType.length === 0) { setError('请选择项目类型'); return; }
    if (form.channel.length === 0) { setError('请选择通道与否'); return; }
    if (form.subsidy.length === 0) { setError('请选择补贴与否'); return; }
    if (!form.quantity || form.quantity <= 0) { setError('请输入有效张数'); return; }
    if (form.tradeMethod.length === 0) { setError('请选择交易方式'); return; }
    if (form.exchange.length === 0) { setError('请选择交易所'); return; }
    if (!form.expiryDate) { setError('请选择信息有效期'); return; }
    setSaving(true);
    setError('');
    try {
      const updated = await profileApi.editSupply(item.id, form);
      onSaved({ ...item, ...(updated as unknown as Partial<PersonalSupply>) });
    } catch {
      setError('保存失败，请重试');
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl p-5 w-full max-w-lg border border-background-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground-950">编辑供给信息</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">年份</label>
            <select value={form.year} onChange={(e) => setForm((p) => ({ ...p, year: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 cursor-pointer">
              <option value="">请选择年份</option>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}年</option>)}
            </select>
          </div>
          {chips('halfYear', '半年', HALF_YEAR_OPTIONS, false)}
          {chips('month', '月份', MONTH_OPTIONS, true)}
          {chips('projectType', '项目类型', PROJECT_TYPE_OPTIONS, false)}
          {chips('channel', '通道与否', CHANNEL_OPTIONS, false)}
          {chips('provinceRequirement', '省份要求', PROVINCE_OPTIONS, true)}
          {chips('subsidy', '补贴与否', SUBSIDY_OPTIONS, false)}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">数量 (个)</label>
            <input type="number" min="1" value={form.quantity || ''} onChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.target.value) }))} onWheel={(e) => e.currentTarget.blur()} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">参考报价 (元/个)</label>
            <input type="text" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} placeholder="选填，例如 35.00" className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400" />
          </div>
          {chips('tradeMethod', '交易方式', TRADE_METHOD_OPTIONS, true)}
          {chips('exchange', '交易所', EXCHANGE_OPTIONS, true)}
          {chips('offerBy', '出价方', OFFER_BY_OPTIONS, true)}
          {chips('tradePrinciple', '交易原则', TRADE_PRINCIPLE_OPTIONS, false)}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">有效期至</label>
            <input type="date" value={form.expiryDate} onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 cursor-pointer" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">补充说明</label>
            <textarea value={form.otherInfo} maxLength={500} onChange={(e) => setForm((p) => ({ ...p, otherInfo: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 h-24 resize-none" />
            <span className="text-xs text-foreground-400">{form.otherInfo.length}/500</span>
          </div>
          {error && <p className="text-accent-600 text-xs">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-medium bg-foreground-950 text-white hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">{saving ? '保存中...' : '保存修改'}</button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">取消</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Card({ item, onEdit, onToggleStatus, onDelete }: {
  item: PersonalSupply;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const projectLabel = formatFieldValue('projectType', item.projectType);
  const channelTag = tagLabel(item.channel);
  const tradeTag = tagLabel(item.tradeMethod);
  const principleTag = tagLabel(item.tradePrinciple);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded">
              {projectLabel}
            </span>
            <span className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded">
              {item.year}年度
            </span>
            {channelTag && (
              <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md">{channelTag}</span>
            )}
            {tradeTag && (
              <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-md">{tradeTag}</span>
            )}
            {principleTag && (
              <span className="px-2 py-0.5 bg-background-200 text-foreground-600 text-xs rounded-md">{principleTag}</span>
            )}
            <span className="text-foreground-400 text-xs">| 发布于 {item.publishDate}</span>
          </div>
          <p className="text-foreground-700 text-sm mt-1 line-clamp-1">{item.otherInfo || '暂无补充信息'}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-400">
            <span>{item.quantity.toLocaleString()} 个</span>
            <span>&yen;{item.price}/个</span>
            <span>有效期至 {item.expiryDate}</span>
            <span>{item.views} 次浏览</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 border border-background-300 text-foreground-600 text-xs rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line mr-1" />编辑
          </button>
          <button
            onClick={onToggleStatus}
            className="px-3 py-1.5 border border-accent-300 text-accent-600 text-xs rounded-md hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-down-circle-line mr-1" />下架
          </button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={onDelete}
                className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                确认
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-1.5 text-foreground-400 text-xs rounded-md hover:text-foreground-600 cursor-pointer"
              >
                取消
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-3 py-1.5 border border-background-300 text-foreground-400 text-xs rounded-md hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1" />删除
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function OfflineCard({ item, onEdit, onRelist, onDelete }: {
  item: PersonalSupply;
  onEdit: () => void;
  onRelist: () => void;
  onDelete: () => void;
}) {
  const projectLabel = formatFieldValue('projectType', item.projectType);
  const channelTag = tagLabel(item.channel);
  const tradeTag = tagLabel(item.tradeMethod);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="bg-background-100 border border-background-200 rounded-lg p-5 opacity-75">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="px-2 py-0.5 bg-foreground-200 text-foreground-600 text-xs font-medium rounded">
              {projectLabel}
            </span>
            <span className="px-2 py-0.5 bg-background-200 text-foreground-500 text-xs rounded">
              {item.year}年度
            </span>
            {channelTag && (
              <span className="px-2 py-0.5 bg-foreground-200 text-foreground-500 text-xs rounded-md">{channelTag}</span>
            )}
            {tradeTag && (
              <span className="px-2 py-0.5 bg-foreground-200 text-foreground-500 text-xs rounded-md">{tradeTag}</span>
            )}
            <span className="text-foreground-400 text-xs">| 发布于 {item.publishDate}</span>
          </div>
          <p className="text-foreground-500 text-sm mt-1 line-clamp-1">{item.otherInfo || '暂无补充信息'}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-400">
            <span>{item.quantity.toLocaleString()} 个</span>
            <span>&yen;{item.price}/个</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 border border-background-300 text-foreground-600 text-xs rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line mr-1" />编辑
          </button>
          <button
            onClick={onRelist}
            className="px-3 py-1.5 border border-primary-300 text-primary-600 text-xs rounded-md hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-up-circle-line mr-1" />重新上架
          </button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button onClick={onDelete} className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 cursor-pointer whitespace-nowrap">确认</button>
              <button onClick={() => setConfirmDelete(false)} className="px-2 py-1.5 text-foreground-400 text-xs rounded-md hover:text-foreground-600 cursor-pointer">取消</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-3 py-1.5 border border-background-300 text-foreground-400 text-xs rounded-md hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1" />删除
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MySupplyTab({ role }: MySupplyTabProps) {
  const [supplies, setSupplies] = useState<PersonalSupply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<PersonalSupply | null>(null);

  useEffect(() => {
    if (role !== 'supplier') {
      setLoading(false);
      return;
    }
    profileApi
      .mySupplies()
      .then((data) => {
        setSupplies(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, [role]);

  const toggleStatus = useCallback((id: number, current: PersonalSupply['status']) => {
    const next = current === 'active' ? 'offline' : 'active';
    profileApi.setSupplyStatus(id, next)
      .then(() => setSupplies((prev) => prev.map((s) => (s.id === id ? { ...s, status: next } : s))))
      .catch(() => {});
  }, []);

  const deleteItem = useCallback((id: number) => {
    profileApi.deleteSupply(id)
      .then(() => setSupplies((prev) => prev.filter((s) => s.id !== id)))
      .catch(() => {});
  }, []);

  if (role !== 'supplier') return null;

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
        <p className="text-foreground-400 text-sm mt-3">加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-foreground-500 text-sm">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
        >
          重新加载
        </button>
      </div>
    );
  }

  const activeList = supplies.filter((s) => s.status === 'active');
  const offlineList = supplies.filter((s) => s.status === 'offline');

  return (
    <div>
      {/* Edit modal */}
      {editingItem && (
        <EditModal
          item={editingItem}
          onSaved={(updated) => { setSupplies((prev) => prev.map((s) => (s.id === updated.id ? updated : s))); setEditingItem(null); }}
          onClose={() => setEditingItem(null)}
        />
      )}

      <div className="mb-8">
        <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
          挂牌中 ({activeList.length})
        </h3>
        {activeList.length === 0 ? (
          <p className="text-foreground-400 text-sm py-8 text-center">暂无挂牌中的供给信息</p>
        ) : (
          <div className="space-y-3">
            {activeList.map((item) => (
              <Card
                key={item.id}
                item={item}
                onEdit={() => setEditingItem(item)}
                onToggleStatus={() => toggleStatus(item.id, item.status)}
                onDelete={() => deleteItem(item.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
          已下架 ({offlineList.length})
        </h3>
        {offlineList.length === 0 ? (
          <p className="text-foreground-400 text-sm py-8 text-center">暂无已下架的信息</p>
        ) : (
          <div className="space-y-3">
            {offlineList.map((item) => (
              <OfflineCard
                key={item.id}
                item={item}
                onEdit={() => setEditingItem(item)}
                onRelist={() => toggleStatus(item.id, item.status)}
                onDelete={() => deleteItem(item.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
